import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { SERVICES } from "../data/services";

// 포트폴리오 — 색인에서 항목을 고르면 상세가 펼쳐진다.
// 사례는 B2B 외주에서 가장 강한 영업자료라, 문의 CTA를 각 상세 끝에 붙인다.
//
// 사례는 관리자 화면에서 등록·수정하므로 서버에서 받아 온다.
//
// 행 전체가 클릭으로 열리지만, 제목은 <Link>(= <a href>) 로 둔다. 예전엔 행이
// div 의 클릭 이벤트뿐이라 검색엔진이 사례 주소를 하나도 따라가지 못했다.

// 사례 → 그 사례를 근거로 쓰는 서비스 페이지. 사례를 본 사람을 서비스로 잇는다.
const SERVICES_BY_CASE = SERVICES.reduce((acc, sv) => {
    sv.cases.forEach((c) => { (acc[c] = acc[c] || []).push(sv); });
    return acc;
}, {});
const PortfolioPage = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const [category, setCategory] = React.useState("전체");
    const [lightbox, setLightbox] = React.useState(null);
    const [portfolio, setPortfolio] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
        // 사례 주소로 바로 들어왔으면 해당 위치로 옮길 것이므로 건드리지 않는다.
        if (!slug) window.scrollTo(0, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        let alive = true;
        fetch("/api/portfolio")
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((data) => {
                if (alive) setPortfolio(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                console.error("포트폴리오 조회 실패", err);
                if (alive) setFailed(true);
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => { alive = false; };
    }, []);

    // 펼친 사례는 URL 이 정한다. /portfolio/{slug} 가 곧 그 사례의 주소다.
    // 사례마다 주소가 있어야 검색에 각각 걸리고, 크몽 상세나 블로그에서
    // 특정 사례로 바로 링크할 수 있다.
    const openItem = React.useMemo(
        () => (slug ? portfolio.find((p) => p.slug === slug) : null),
        [slug, portfolio]
    );

    // slug 가 비어 있는 사례(관리자가 새로 등록한 건)는 주소를 만들 수 없어
    // 예전처럼 화면 안에서만 펼친다.
    const [fallbackOpen, setFallbackOpen] = React.useState(null);

    const openId = openItem ? openItem.id : null;

    const toggle = (p) => {
        if (openId === p.id) navigate("/portfolio");
        else if (p.slug) navigate(`/portfolio/${p.slug}`);
        else setFallbackOpen((prev) => (prev === p.id ? null : p.id));
    };

    // 주소로 바로 들어온 경우, 해당 사례가 화면에 보이게 옮겨 준다.
    const openRef = React.useRef(null);
    React.useEffect(() => {
        if (!openItem || !openRef.current) return;
        openRef.current.scrollIntoView({ block: "start", behavior: "auto" });
    }, [openItem]);

    const CATEGORIES = React.useMemo(
        () => ["전체", ...Array.from(new Set(portfolio.map((p) => p.category).filter(Boolean)))],
        [portfolio]
    );

    const go = (path) => {
        navigate(path);
        window.scrollTo(0, 0);
    };

    const list = category === "전체" ? portfolio : portfolio.filter((p) => p.category === category);

    return (
        <>
            <div className="sch-ph">
                <div className="sch-wrap">
                    <div className="sch-no">PORTFOLIO — 수행 실적</div>
                    <h1>포트폴리오</h1>
                    <p className="sc-body-lg">
                        직접 개발해 납품한 자동화 프로젝트입니다. 항목을 누르면 어떤 문제를
                        어떻게 풀었는지 상세를 볼 수 있습니다.
                    </p>
                </div>
            </div>

            <div className="sch-wrap" style={{ paddingTop: "36px", paddingBottom: "72px" }}>
                {CATEGORIES.length > 2 && (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "26px" }}>
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                type="button"
                                className={`sc-chip${category === c ? " sc-chip--active" : ""}`}
                                style={{ borderRadius: 0 }}
                                onClick={() => { setCategory(c); setFallbackOpen(null); if (slug) navigate("/portfolio"); }}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                )}

                {loading ? (
                    <p className="sc-caption">불러오는 중…</p>
                ) : failed ? (
                    <div className="sc-empty">
                        <div className="sc-empty__title">사례를 불러오지 못했습니다</div>
                        <div className="sc-empty__desc">
                            잠시 후 다시 시도해 주세요.{" "}
                            <button className="sch-inlinelink" onClick={() => go("/custom-product-first")}>
                                문의로 바로 가기 ↗
                            </button>
                        </div>
                    </div>
                ) : list.length === 0 ? (
                    <div className="sc-empty">
                        <div className="sc-empty__title">등록된 사례가 없습니다</div>
                    </div>
                ) : (
                    <div className="sch-pi">
                        {list.map((p, i) => {
                            const open = openId === p.id || fallbackOpen === p.id;
                            return (
                                <React.Fragment key={p.id}>
                                    <div
                                        className="sch-pirow"
                                        role="button"
                                        aria-expanded={open}
                                        ref={openId === p.id ? openRef : null}
                                        onClick={() => toggle(p)}
                                    >
                                        <span className="sch-code">
                                            W-{String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="sch-nm">
                                            {p.slug ? (
                                                <Link
                                                    to={`/portfolio/${p.slug}`}
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(p); }}
                                                    style={{ color: "inherit", textDecoration: "none" }}
                                                >
                                                    {p.title}
                                                </Link>
                                            ) : p.title}
                                        </span>
                                        <span className="sch-ds">{p.summary}</span>
                                        <span className="sch-tg">{p.category}</span>
                                        <span className="sch-ar">{open ? "−" : "→"}</span>
                                    </div>

                                    {open && (
                                        <article className="sch-pfdetail">
                                            <dl className="sch-pfmeta">
                                                {p.client && (<><dt>발주처</dt><dd>{p.client}</dd></>)}
                                                {p.period && (<><dt>기간</dt><dd>{p.period}</dd></>)}
                                                {p.budget && (<><dt>규모</dt><dd>{p.budget}</dd></>)}
                                                {p.role && (<><dt>역할</dt><dd>{p.role}</dd></>)}
                                            </dl>

                                            {p.stack?.length > 0 && (
                                                <div className="sch-pfstack">
                                                    {p.stack.map((t) => (
                                                        <span key={t} className="sc-badge sc-badge--best">{t}</span>
                                                    ))}
                                                </div>
                                            )}

                                            {p.problem && (
                                                <section>
                                                    <h3 className="sc-h4">문제</h3>
                                                    <p>{p.problem}</p>
                                                </section>
                                            )}
                                            {p.solution && (
                                                <section>
                                                    <h3 className="sc-h4">해결</h3>
                                                    <p>{p.solution}</p>
                                                </section>
                                            )}
                                            {p.result && (
                                                <section>
                                                    <h3 className="sc-h4">성과</h3>
                                                    <p>{p.result}</p>
                                                </section>
                                            )}

                                            {p.images?.length > 0 && (
                                                <section>
                                                    <h3 className="sc-h4">화면</h3>
                                                    <div className="sch-pfshots">
                                                        {p.images.map((img) => (
                                                            <button
                                                                type="button"
                                                                key={img.src}
                                                                className="sch-pfshot"
                                                                onClick={() => setLightbox(img)}
                                                                aria-label={`${img.alt || p.title} 크게 보기`}
                                                            >
                                                                <img src={img.src} alt={img.alt || p.title} loading="lazy" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </section>
                                            )}

                                            {p.link && (
                                                <p className="sch-mono" style={{ marginTop: "16px" }}>
                                                    <a
                                                        className="sch-inlinelink"
                                                        href={p.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        위시켓 원본 보기 ↗
                                                    </a>
                                                </p>
                                            )}

                                            {SERVICES_BY_CASE[p.slug]?.length > 0 && (
                                                <p className="sch-mono" style={{ marginTop: "16px" }}>
                                                    관련 분야:{" "}
                                                    {SERVICES_BY_CASE[p.slug].map((sv, k) => (
                                                        <React.Fragment key={sv.slug}>
                                                            {k > 0 && " · "}
                                                            <Link className="sch-inlinelink" to={`/service/${sv.slug}`}>{sv.nav}</Link>
                                                        </React.Fragment>
                                                    ))}
                                                </p>
                                            )}

                                            <div className="sch-sectioncta" style={{ marginTop: "18px" }}>
                                                <span>비슷한 작업이 필요하신가요?</span>
                                                <button className="sc-btn" onClick={() => go("/custom-product-first")}>
                                                    무료 견적 받기
                                                </button>
                                            </div>
                                        </article>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}

                {lightbox && (
                    <div className="sc-modal-backdrop" onClick={() => setLightbox(null)}>
                        <figure className="sch-lightbox" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="sc-modal__x sch-lightbox__x"
                                onClick={() => setLightbox(null)}
                                aria-label="닫기"
                            >
                                ✕
                            </button>
                            <img src={lightbox.src} alt={lightbox.alt || ""} />
                            {lightbox.alt && <figcaption>{lightbox.alt}</figcaption>}
                        </figure>
                    </div>
                )}

                <p className="sch-mono" style={{ marginTop: "22px" }}>
                    * 계약상 공개가 어려운 사례는 제외했습니다 —{" "}
                    <button className="sch-inlinelink" onClick={() => go("/custom-product-first")}>
                        비슷한 사례 문의하기 ↗
                    </button>
                </p>
            </div>
        </>
    );
};

export default PortfolioPage;
